import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  FileText, Camera, Pen, Car, Plus, Trash2, Download, Send,
  Upload, Check, X, RefreshCw, Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ========== EINSATZBERICHT VIEW ========== */
export const EinsatzberichtView = ({ auftraege }) => {
  const [berichte, setBerichte] = useState([]);
  const [selectedAuftrag, setSelectedAuftrag] = useState("");
  const [fotos, setFotos] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [zusatzNotizen, setZusatzNotizen] = useState("");
  const [selectedBericht, setSelectedBericht] = useState(null);
  const [sendEmail, setSendEmail] = useState("");
  const [sending, setSending] = useState(false);

  const loadBerichte = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/einsatzbericht/berichte`);
      setBerichte(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadFotos = useCallback(async (auftragId) => {
    if (!auftragId) return;
    try {
      const res = await axios.get(`${API}/einsatzbericht/auftraege/${auftragId}/fotos`);
      setFotos(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadBerichte(); }, [loadBerichte]);
  useEffect(() => { if (selectedAuftrag) loadFotos(selectedAuftrag); }, [selectedAuftrag, loadFotos]);

  const handleFotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.size > 7 * 1024 * 1024) { toast.error(`${file.name} ist zu groß (max. 7 MB)`); continue; }
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        try {
          await axios.post(`${API}/einsatzbericht/auftraege/${selectedAuftrag}/fotos`, {
            filename: file.name, content_type: file.type, data_base64: base64,
          });
          toast.success(`${file.name} hochgeladen`);
          loadFotos(selectedAuftrag);
        } catch (err) { toast.error("Upload fehlgeschlagen"); }
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const deleteFoto = async (fotoId) => {
    try {
      await axios.delete(`${API}/einsatzbericht/fotos/${fotoId}`);
      loadFotos(selectedAuftrag);
    } catch (err) { toast.error("Löschen fehlgeschlagen"); }
  };

  const generateBericht = async () => {
    if (!selectedAuftrag) { toast.error("Bitte einen Auftrag auswählen"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/einsatzbericht/generieren`, {
        auftrag_id: selectedAuftrag, zusatz_notizen: zusatzNotizen,
      });
      toast.success("Einsatzbericht erstellt!");
      setSelectedBericht(res.data);
      setZusatzNotizen("");
      loadBerichte();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei der Berichtserstellung");
    } finally { setGenerating(false); }
  };

  const downloadPdf = async (berichtId) => {
    try {
      const res = await axios.get(`${API}/einsatzbericht/berichte/${berichtId}/pdf`);
      const { html, filename } = res.data;
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename.replace(".pdf", ".html"); a.click();
      URL.revokeObjectURL(url);
      toast.success("Bericht heruntergeladen");
    } catch (err) { toast.error("Download fehlgeschlagen"); }
  };

  const sendBericht = async (berichtId) => {
    if (!sendEmail) { toast.error("Bitte E-Mail-Adresse eingeben"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/einsatzbericht/berichte/${berichtId}/senden`, {
        empfaenger_email: sendEmail,
      });
      toast.success(`Bericht an ${sendEmail} gesendet`);
      setSendEmail("");
      loadBerichte();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
    } finally { setSending(false); }
  };

  const deleteBericht = async (berichtId) => {
    try {
      await axios.delete(`${API}/einsatzbericht/berichte/${berichtId}`);
      toast.success("Bericht gelöscht");
      if (selectedBericht?.id === berichtId) setSelectedBericht(null);
      loadBerichte();
    } catch (err) { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--brand-ink)] flex items-center gap-2">
          <FileText className="w-5 h-5" /> KI-Einsatzbericht
        </h2>
      </div>

      {/* Bericht erstellen */}
      <Card className="p-5 border-[var(--brand-border)]">
        <h3 className="font-semibold text-sm mb-3">Neuen Bericht erstellen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Auftrag auswählen</Label>
            <select
              value={selectedAuftrag}
              onChange={e => setSelectedAuftrag(e.target.value)}
              className="w-full h-9 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm mt-1"
            >
              <option value="">— Auftrag wählen —</option>
              {auftraege.map(a => (
                <option key={a.id} value={a.id}>{a.titel} ({a.datum})</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Zusätzliche Notizen (optional)</Label>
            <Textarea
              value={zusatzNotizen}
              onChange={e => setZusatzNotizen(e.target.value)}
              placeholder="z.B. Besonderheiten, Probleme, Wetterbedingungen..."
              className="mt-1 h-20"
            />
          </div>
        </div>

        {/* Foto-Upload */}
        {selectedAuftrag && (
          <div className="mt-4">
            <Label className="text-xs flex items-center gap-1"><Camera className="w-3 h-3" /> Fotos hochladen</Label>
            <div className="flex items-center gap-2 mt-1">
              <label className="cursor-pointer flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-dashed border-[var(--brand-border)] text-sm text-[var(--brand-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition">
                <Upload className="w-4 h-4" /> Fotos auswählen
                <input type="file" accept="image/*" multiple onChange={handleFotoUpload} className="hidden" />
              </label>
              <span className="text-xs text-[var(--brand-muted)]">{fotos.length} Foto(s) hochgeladen</span>
            </div>
            {fotos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {fotos.map(f => (
                  <div key={f.id} className="relative group">
                    <div className="w-16 h-16 rounded border border-[var(--brand-border)] bg-gray-50 flex items-center justify-center text-xs text-[var(--brand-muted)]">
                      <Camera className="w-5 h-5" />
                    </div>
                    <button onClick={() => deleteFoto(f.id)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <X className="w-3 h-3" />
                    </button>
                    <p className="text-[9px] text-center truncate w-16">{f.filename}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Button
          onClick={generateBericht}
          disabled={!selectedAuftrag || generating}
          className="mt-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
        >
          {generating ? <><RefreshCw className="w-4 h-4 animate-spin mr-1" /> KI generiert...</> : <><Sparkles className="w-4 h-4 mr-1" /> Bericht mit KI erstellen</>}
        </Button>
      </Card>

      {/* Aktueller Bericht */}
      {selectedBericht && (
        <Card className="p-5 border-[var(--brand-border)] border-l-4 border-l-green-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm flex items-center gap-1"><Check className="w-4 h-4 text-green-600" /> Generierter Bericht</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{selectedBericht.status}</span>
          </div>
          <div className="text-xs text-[var(--brand-muted)] mb-2">
            {selectedBericht.auftrag_titel} · {selectedBericht.kunde_name} · {selectedBericht.datum}
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-sm whitespace-pre-wrap">{selectedBericht.text}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => downloadPdf(selectedBericht.id)}>
              <Download className="w-3 h-3 mr-1" /> PDF
            </Button>
            <div className="flex items-center gap-1">
              <Input
                value={sendEmail}
                onChange={e => setSendEmail(e.target.value)}
                placeholder="E-Mail des Kunden"
                className="h-8 w-48 text-xs"
              />
              <Button size="sm" onClick={() => sendBericht(selectedBericht.id)} disabled={sending}>
                <Send className="w-3 h-3 mr-1" /> {sending ? "..." : "Senden"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Alle Berichte */}
      {berichte.length > 0 && (
        <Card className="p-5 border-[var(--brand-border)]">
          <h3 className="font-semibold text-sm mb-3">Alle Einsatzberichte ({berichte.length})</h3>
          <div className="space-y-2">
            {berichte.map(b => (
              <div key={b.id} className="flex items-center justify-between p-3 rounded-lg border border-[var(--brand-border)] hover:bg-gray-50">
                <div className="flex-1">
                  <div className="text-sm font-medium">{b.auftrag_titel}</div>
                  <div className="text-xs text-[var(--brand-muted)]">{b.kunde_name} · {b.datum} · {b.mitarbeiter?.join(", ")}</div>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === "gesendet" ? "bg-blue-100 text-blue-700" : b.status === "freigegeben" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {b.status}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedBericht(b)}><FileText className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(b.id)}><Download className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteBericht(b.id)} className="text-red-500"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

/* ========== DIGITALE UNTERSCHRIFT VIEW ========== */
export const UnterschriftView = ({ auftraege }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedAuftrag, setSelectedAuftrag] = useState("");
  const [unterzeichnerName, setUnterzeichnerName] = useState("");
  const [unterzeichnerRolle, setUnterzeichnerRolle] = useState("kunde");
  const [signatures, setSignatures] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadSignatures = useCallback(async () => {
    if (!selectedAuftrag) return;
    try {
      const res = await axios.get(`${API}/unterschrift/auftrag/${selectedAuftrag}`);
      setSignatures(res.data || []);
    } catch (err) { console.error(err); }
  }, [selectedAuftrag]);

  useEffect(() => { if (selectedAuftrag) loadSignatures(); }, [selectedAuftrag, loadSignatures]);

  // Canvas drawing
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoords(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#1f2328";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  };

  const stopDrawing = () => { setIsDrawing(false); };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    if (!selectedAuftrag) { toast.error("Bitte Auftrag auswählen"); return; }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    const base64 = dataUrl.split(",")[1];

    // Check if canvas is empty
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const isEmpty = !imageData.data.some((channel, i) => i % 4 !== 3 ? channel !== 0 : channel !== 0);
    if (isEmpty) { toast.error("Bitte zuerst unterschreiben"); return; }

    setSaving(true);
    try {
      await axios.post(`${API}/unterschrift/speichern`, {
        auftrag_id: selectedAuftrag,
        unterschrift_base64: base64,
        unterzeichner_name: unterzeichnerName,
        unterzeichner_rolle: unterzeichnerRolle,
      });
      toast.success("Unterschrift gespeichert!");
      clearCanvas();
      setUnterzeichnerName("");
      loadSignatures();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const deleteSignature = async (id) => {
    try {
      await axios.delete(`${API}/unterschrift/${id}`);
      toast.success("Unterschrift gelöscht");
      loadSignatures();
    } catch (err) { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-[var(--brand-ink)] flex items-center gap-2">
        <Pen className="w-5 h-5" /> Digitale Kundenunterschrift
      </h2>

      <Card className="p-5 border-[var(--brand-border)]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-xs">Auftrag</Label>
            <select
              value={selectedAuftrag}
              onChange={e => setSelectedAuftrag(e.target.value)}
              className="w-full h-9 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm mt-1"
            >
              <option value="">— Auftrag wählen —</option>
              {auftraege.map(a => (
                <option key={a.id} value={a.id}>{a.titel} ({a.datum})</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Name des Unterzeichners</Label>
            <Input
              value={unterzeichnerName}
              onChange={e => setUnterzeichnerName(e.target.value)}
              placeholder="z.B. Max Mustermann"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Rolle</Label>
            <select
              value={unterzeichnerRolle}
              onChange={e => setUnterzeichnerRolle(e.target.value)}
              className="w-full h-9 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm mt-1"
            >
              <option value="kunde">Kunde / Auftraggeber</option>
              <option value="mitarbeiter">Mitarbeiter</option>
            </select>
          </div>
        </div>

        {/* Signature Canvas */}
        <div className="border-2 border-dashed border-[var(--brand-border)] rounded-lg p-2 bg-white">
          <p className="text-xs text-[var(--brand-muted)] mb-1 text-center">Hier unterschreiben (mit Maus oder Finger)</p>
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            className="w-full border border-gray-200 rounded cursor-crosshair touch-none"
            style={{ maxWidth: "600px", height: "200px" }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>

        <div className="flex gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={clearCanvas}>
            <X className="w-3 h-3 mr-1" /> Löschen
          </Button>
          <Button size="sm" onClick={saveSignature} disabled={saving || !selectedAuftrag} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
            <Check className="w-3 h-3 mr-1" /> {saving ? "Speichert..." : "Unterschrift speichern"}
          </Button>
        </div>
      </Card>

      {/* Gespeicherte Unterschriften */}
      {signatures.length > 0 && (
        <Card className="p-5 border-[var(--brand-border)]">
          <h3 className="font-semibold text-sm mb-3">Gespeicherte Unterschriften</h3>
          <div className="space-y-3">
            {signatures.map(s => (
              <div key={s.id} className="flex items-center gap-4 p-3 rounded-lg border border-[var(--brand-border)]">
                <img
                  src={`data:image/png;base64,${s.unterschrift_base64}`}
                  alt="Unterschrift"
                  className="h-12 border border-gray-200 rounded bg-white"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">{s.unterzeichner_name || "Unbekannt"}</div>
                  <div className="text-xs text-[var(--brand-muted)]">
                    {s.unterzeichner_rolle === "kunde" ? "Kunde" : "Mitarbeiter"} · {new Date(s.erstellt_am).toLocaleString("de-DE")}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => deleteSignature(s.id)} className="text-red-500">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

/* ========== FAHRZEUGE VIEW ========== */
export const FahrzeugeView = ({ auftraege }) => {
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ kennzeichen: "", bezeichnung: "", typ: "", farbe: "", notizen: "" });
  const [editId, setEditId] = useState(null);
  const [assignAuftrag, setAssignAuftrag] = useState("");
  const [assignFahrzeug, setAssignFahrzeug] = useState("");

  const loadFahrzeuge = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fahrzeuge`);
      setFahrzeuge(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadFahrzeuge(); }, [loadFahrzeuge]);

  const handleSave = async () => {
    try {
      if (editId) {
        await axios.put(`${API}/fahrzeuge/${editId}`, form);
        toast.success("Fahrzeug aktualisiert");
      } else {
        await axios.post(`${API}/fahrzeuge`, form);
        toast.success("Fahrzeug angelegt");
      }
      setShowForm(false);
      setForm({ kennzeichen: "", bezeichnung: "", typ: "", farbe: "", notizen: "" });
      setEditId(null);
      loadFahrzeuge();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/fahrzeuge/${id}`);
      toast.success("Fahrzeug gelöscht");
      loadFahrzeuge();
    } catch (err) { toast.error("Löschen fehlgeschlagen"); }
  };

  const handleAssign = async () => {
    if (!assignAuftrag || !assignFahrzeug) { toast.error("Bitte Auftrag und Fahrzeug wählen"); return; }
    try {
      await axios.post(`${API}/fahrzeuge/auftrag/${assignAuftrag}/zuweisen`, { fahrzeug_id: assignFahrzeug });
      toast.success("Fahrzeug zugewiesen");
      setAssignAuftrag("");
      setAssignFahrzeug("");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--brand-ink)] flex items-center gap-2">
          <Car className="w-5 h-5" /> Fahrzeugverwaltung
        </h2>
        <Button size="sm" onClick={() => { setShowForm(true); setEditId(null); setForm({ kennzeichen: "", bezeichnung: "", typ: "", farbe: "", notizen: "" }); }} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
          <Plus className="w-3 h-3 mr-1" /> Fahrzeug anlegen
        </Button>
      </div>

      {/* Fahrzeug-Formular */}
      {showForm && (
        <Card className="p-5 border-[var(--brand-border)]">
          <h3 className="font-semibold text-sm mb-3">{editId ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Kennzeichen *</Label>
              <Input value={form.kennzeichen} onChange={e => setForm(f => ({ ...f, kennzeichen: e.target.value }))} placeholder="z.B. M-AB 1234" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Bezeichnung</Label>
              <Input value={form.bezeichnung} onChange={e => setForm(f => ({ ...f, bezeichnung: e.target.value }))} placeholder="z.B. VW Crafter" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Typ</Label>
              <select value={form.typ} onChange={e => setForm(f => ({ ...f, typ: e.target.value }))} className="w-full h-9 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm mt-1">
                <option value="">— Typ wählen —</option>
                <option value="PKW">PKW</option>
                <option value="Transporter">Transporter</option>
                <option value="LKW">LKW</option>
                <option value="Anhänger">Anhänger</option>
                <option value="Sonstige">Sonstige</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Farbe</Label>
              <Input value={form.farbe} onChange={e => setForm(f => ({ ...f, farbe: e.target.value }))} placeholder="z.B. Weiß" className="mt-1" />
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">Notizen</Label>
            <Textarea value={form.notizen} onChange={e => setForm(f => ({ ...f, notizen: e.target.value }))} placeholder="z.B. TÜV bis 03/2027" className="mt-1 h-16" />
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleSave} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
              {editId ? "Aktualisieren" : "Anlegen"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Abbrechen</Button>
          </div>
        </Card>
      )}

      {/* Fahrzeug einem Auftrag zuweisen */}
      <Card className="p-5 border-[var(--brand-border)]">
        <h3 className="font-semibold text-sm mb-3">Fahrzeug einem Auftrag zuweisen</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Auftrag</Label>
            <select value={assignAuftrag} onChange={e => setAssignAuftrag(e.target.value)} className="w-full h-9 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm mt-1">
              <option value="">— Auftrag wählen —</option>
              {auftraege.map(a => (<option key={a.id} value={a.id}>{a.titel} ({a.datum})</option>))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Fahrzeug</Label>
            <select value={assignFahrzeug} onChange={e => setAssignFahrzeug(e.target.value)} className="w-full h-9 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm mt-1">
              <option value="">— Fahrzeug wählen —</option>
              {fahrzeuge.map(f => (<option key={f.id} value={f.id}>{f.kennzeichen} – {f.bezeichnung}</option>))}
            </select>
          </div>
          <Button size="sm" onClick={handleAssign} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
            Zuweisen
          </Button>
        </div>
      </Card>

      {/* Fahrzeug-Liste */}
      {fahrzeuge.length > 0 ? (
        <Card className="p-5 border-[var(--brand-border)]">
          <h3 className="font-semibold text-sm mb-3">Alle Fahrzeuge ({fahrzeuge.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {fahrzeuge.map(f => (
              <div key={f.id} className="p-3 rounded-lg border border-[var(--brand-border)] hover:shadow-sm transition">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-[var(--brand-primary)]" />
                    <span className="font-mono font-semibold text-sm">{f.kennzeichen}</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditId(f.id); setForm({ kennzeichen: f.kennzeichen, bezeichnung: f.bezeichnung, typ: f.typ, farbe: f.farbe, notizen: f.notizen }); setShowForm(true); }} className="p-1 hover:bg-gray-100 rounded">
                      <Pen className="w-3 h-3 text-[var(--brand-muted)]" />
                    </button>
                    <button onClick={() => handleDelete(f.id)} className="p-1 hover:bg-red-50 rounded">
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="text-sm mt-1">{f.bezeichnung}</div>
                <div className="text-xs text-[var(--brand-muted)]">
                  {f.typ}{f.farbe ? ` · ${f.farbe}` : ""}
                </div>
                {f.notizen && <p className="text-xs text-[var(--brand-muted)] mt-1 italic">{f.notizen}</p>}
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-8 border-[var(--brand-border)] text-center">
          <Car className="w-8 h-8 text-[var(--brand-muted)] mx-auto mb-2" />
          <p className="text-sm text-[var(--brand-muted)]">Noch keine Fahrzeuge angelegt.</p>
        </Card>
      )}
    </div>
  );
};
